import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { linkedInApi } from "@/services/linkedin-api";
import { LinkedInPerfilSimulado } from "./LinkedInPerfilSimulado";
import { marcasDoDiff, urlDoPerfil } from "./linkedinPerfil";

interface CandidatoLinkedInTabProps {
  freelancerId: string;
  /** URL cadastrada na ficha: a última opção para o link do nome. */
  linkedinUrl: string | null;
  /** Leitura da fila de revisões; ausente, a aba mostra o perfil aplicado. */
  linkedinJobId?: string | null;
}

function useLinkedInAtual(freelancerId: string, enabled = true) {
  return useQuery({
    queryKey: ["freelancer-linkedin-atual", freelancerId],
    queryFn: () => linkedInApi.getFreelancerAtual(freelancerId),
    enabled,
  });
}

/**
 * A leitura que está na fila de revisões, com o que mudou desde a última
 * aplicada. Sem foto ou capa nesta leitura (PDF), usa as do perfil aplicado.
 */
export function LeituraEmRevisao({ freelancerId, jobId, linkedinUrl }: { freelancerId: string; jobId: string; linkedinUrl: string | null }) {
  const { data: job, isLoading } = useQuery({
    queryKey: ["linkedin-job", jobId],
    queryFn: () => linkedInApi.getJob(jobId),
    staleTime: 60 * 1000,
  });
  const atual = useLinkedInAtual(freelancerId, !!job && (!job.photo_url || !job.cover_url));
  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!job?.extracted_json) return <p className="text-sm text-muted-foreground">Esta leitura ainda não tem o perfil extraído.</p>;
  const marcas = marcasDoDiff(job.diff_summary);
  const url = urlDoPerfil(job.extracted_json.linkedin_url, job.source === "scraping" ? job.original_filename : null, linkedinUrl);
  return (
    <LinkedInPerfilSimulado
      perfil={job.extracted_json}
      marcas={marcas}
      url={url}
      pdfUrl={job.source === "pdf_upload" ? job.s3_url : null}
      lidoEm={job.extracted_at ?? job.created_at}
      legenda={job.status === "review_ready" ? "Leitura em revisão" : "Leitura do LinkedIn"}
      primeiraLeitura={!marcas}
      fotoUrl={job.photo_url ?? atual.data?.photo_url}
      capaUrl={job.cover_url ?? atual.data?.cover_url}
    />
  );
}

/** O último perfil aplicado na ficha, sem marcas: não há o que comparar. */
function PerfilAplicado({ freelancerId, linkedinUrl }: { freelancerId: string; linkedinUrl: string | null }) {
  const atual = useLinkedInAtual(freelancerId);
  const historyId = atual.data?.history_id ?? null;
  const entrada = useQuery({
    queryKey: ["linkedin-history-entry", historyId],
    queryFn: () => linkedInApi.getHistoryEntry(historyId!),
    enabled: !!historyId,
    staleTime: 5 * 60 * 1000,
  });
  if (atual.isLoading || entrada.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!atual.data || !entrada.data) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">A plataforma ainda não leu o LinkedIn deste freelancer.</p>
        <ExternalLinkButton kind="linkedin" url={linkedinUrl} variant="button" label="Abrir no LinkedIn" hideWhenEmpty={false} className="h-8 text-xs" />
      </div>
    );
  }
  return (
    <LinkedInPerfilSimulado
      perfil={entrada.data.extracted_json}
      marcas={null}
      url={urlDoPerfil(atual.data.linkedin_url, entrada.data.extracted_json.linkedin_url, linkedinUrl)}
      pdfUrl={atual.data.pdf_url}
      lidoEm={atual.data.atualizado_em ?? entrada.data.applied_at}
      legenda="Perfil aplicado na ficha"
      fotoUrl={atual.data.photo_url}
      capaUrl={atual.data.cover_url}
    />
  );
}

/**
 * Aba LinkedIn do painel: aberto da fila de revisões, mostra a leitura em
 * revisão; aberto de qualquer lista, o último perfil aplicado.
 */
export function CandidatoLinkedInTab({ freelancerId, linkedinUrl, linkedinJobId }: CandidatoLinkedInTabProps) {
  return linkedinJobId ? (
    <LeituraEmRevisao freelancerId={freelancerId} jobId={linkedinJobId} linkedinUrl={linkedinUrl} />
  ) : (
    <PerfilAplicado freelancerId={freelancerId} linkedinUrl={linkedinUrl} />
  );
}
